import {
  type CSSProperties,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
  type MutableRefObject,
  type ReactNode,
  forwardRef,
  useEffect,
} from 'react'

import {
  Dialog as BaseDialog,
  DialogBackdrop,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react'

import { Button } from '@/components/ui/button.tsx'
import { HIDE_ANIMATION_DURATION } from '@/dialogs/constants.ts'
import { classNames } from '@/utils/helpers.ts'
import { delay, noop } from '@/utils/misc.ts'

import CloseIcon from '../../assets/icons/icon-close.svg'

export interface ModalProps {
  show: boolean
  onClose?: (() => void) | null
  footer?: ReactNode
  title?: ReactNode
  description?: ReactNode | ReactNode[]
  panelClassName?: string
  bigDialog?: boolean
  wideXl?: boolean
  wideXxl?: boolean
  children?: ReactNode
  containerClassName?: string
  closeIconClassName?: string
  afterLeave?: () => void
  rootClassName?: string
  showCloseIcon?: boolean
  childrenClassName?: string
  className?: string
  panelStyle?: CSSProperties
  testId?: string
  initialFocus?: MutableRefObject<HTMLElement | null>
}

export const Dialog = (props: ModalProps) => {
  const {
    title,
    description,
    footer,
    show,
    children,
    panelClassName,
    bigDialog,
    onClose,
    wideXl,
    wideXxl,
    containerClassName,
    closeIconClassName,
    rootClassName,
    afterLeave,
    showCloseIcon = true,
    childrenClassName,
    className,
    panelStyle,
    testId,
    initialFocus,
  } = props

  useEffect(() => {
    if (show && initialFocus) {
      const focuInput = async () => {
        await delay(500)
        initialFocus.current?.focus()
      }

      focuInput()
    }
  }, [initialFocus, show])

  return (
    <Transition
      appear
      show={show}
      afterLeave={afterLeave}
    >
      <BaseDialog
        onClose={onClose ?? noop}
        className={classNames('fixed inset-0 z-[60]', className)}
      >
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave={`ease-in duration-${HIDE_ANIMATION_DURATION}`}
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className="bg-secondary-900/30 dark:bg-secondary-900/60 pointer-events-none fixed inset-0 transition-opacity"
          as={DialogBackdrop}
        />

        <div
          className={classNames('fixed inset-0 overflow-y-auto', rootClassName)}
        >
          <div
            className={classNames(
              'flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0',
              containerClassName,
            )}
          >
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-6 sm:translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave={`ease-in duration-${HIDE_ANIMATION_DURATION}`}
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-6 sm:translate-y-4 sm:scale-95"
            >
              <DialogPanel
                className={classNames(
                  'bg-card dark:border-border-default/50 pointer-events-auto relative w-full transform overflow-hidden rounded-lg px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:w-full sm:p-6 dark:border dark:shadow-2xl',
                  { 'sm:max-w-lg': !panelClassName?.includes('sm:max-w-') },
                  bigDialog
                    ? `fixed grid w-full max-w-lg gap-6 border p-8 shadow-lg duration-${HIDE_ANIMATION_DURATION} data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg md:w-full ${
                        wideXl
                          ? 'lg:max-w-5xl'
                          : wideXxl
                            ? 'lg:max-w-6xl'
                            : 'lg:max-w-3xl'
                      }`
                    : '',
                  panelClassName,
                )}
                style={panelStyle}
                data-testid={testId}
              >
                {onClose && showCloseIcon && (
                  <div
                    className={classNames(
                      'absolute right-4 top-4',
                      closeIconClassName,
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onClose()}
                      className="text-secondary-300 group p-0"
                    >
                      <CloseIcon className="h-4 w-4 group-hover:opacity-80" />
                    </button>
                  </div>
                )}
                {(title || description) && (
                  <DialogHeader>
                    {title && <DialogTitle>{title}</DialogTitle>}
                    {description && (
                      <DialogDescription>{description}</DialogDescription>
                    )}
                  </DialogHeader>
                )}
                {children && (
                  <div className={childrenClassName}>{children}</div>
                )}
                {footer && <DialogFooter>{footer}</DialogFooter>}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </BaseDialog>
    </Transition>
  )
}

const DialogHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    {...props}
    className={classNames(
      'flex flex-col space-y-2 text-center sm:text-left',
      className,
    )}
  />
)

const DialogFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    {...props}
    className={classNames(
      'mt-6 flex flex-col-reverse gap-4 sm:flex-row sm:justify-end',
      className,
    )}
  />
)

interface ClosableDialogFooterProps extends HTMLAttributes<HTMLDivElement> {
  onClose: (() => void) | undefined | null
  closeText?: string
  buttonClassName?: string
  isLoading?: boolean
  disabled?: boolean
}

const ClosableDialogFooter = ({
  onClose,
  children,
  className,
  closeText,
  buttonClassName,
  isLoading,
  disabled,
  ...props
}: ClosableDialogFooterProps) => (
  <Dialog.Footer
    className={classNames('grid grid-cols-2 gap-4', className)}
    {...props}
  >
    <Button
      variant="outline"
      type="button"
      className={buttonClassName}
      isLoading={isLoading}
      disabled={disabled}
      onClick={() => {
        if (onClose) {
          onClose()
        }
      }}
      testId="dialog-close-button"
    >
      {closeText ?? 'Cancel'}
    </Button>
    {children}
  </Dialog.Footer>
)

const DialogTitle = forwardRef<
  ElementRef<'span'>,
  ComponentPropsWithoutRef<'span'>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={classNames(
      'text-title text-center text-2xl font-semibold leading-none tracking-tight',
      className,
    )}
    data-testid="dialog-title"
    {...props}
  />
))

DialogTitle.displayName = 'DialogTitle'

const DialogDescription = forwardRef<
  ElementRef<'span'>,
  ComponentPropsWithoutRef<'span'>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={classNames(
      'text-secondary-300 text-sm font-normal tracking-tight',
      className,
    )}
    {...props}
  />
))

DialogDescription.displayName = 'DialogDescription'

Dialog.Description = DialogDescription
Dialog.Title = DialogTitle
Dialog.Header = DialogHeader
Dialog.Footer = DialogFooter
Dialog.ClosableFooter = ClosableDialogFooter
